import { motion } from 'framer-motion';
import styled, { css } from 'styled-components';

import bgImage from '@pkg/assets/images/yat-dashboard-mobile-bg.png';
import { LayoutPosition, styledLayout, styledReset } from '@pkg/primitives';

import {
    cardTransition,
    CardVariant,
    cardVariants,
    modalWrapperTransition,
    ModalWrapperVariant,
    modalWrapperVariants,
} from './Modal.motion';
import { ModalCloseButtonPositions, type ModalPaddingSizes, ModalVariants } from './Modal.types';
import {
    MODAL_BORDER_OFFSET,
    MODAL_BORDER_RADIUS,
    MODAL_CARD_MAX_WIDTH,
    MODAL_CLOSE_OFFSET,
    MODAL_LAYOUT_SPACING,
    MODAL_MIN_GUTTER,
    MODAL_PADDING_SIZES,
    TEMP_ZINDEX,
} from './tokens';

interface FullScreenMobile {
    $fullScreenMobile?: boolean;
    $mobileDarkBackground?: boolean;
}
interface CardProps extends FullScreenMobile {
    $maxHeight?: number;
    $maxWidth?: string;
    $variant?: ModalVariants;
}

export const stackingContext = {
    backdrop: TEMP_ZINDEX + 1,
    cardWrapper: TEMP_ZINDEX + 2,
    card: TEMP_ZINDEX + 3,
    cardBefore: TEMP_ZINDEX + 4,
    cardAfter: TEMP_ZINDEX + 5,
    interior: TEMP_ZINDEX + 6,
    content: TEMP_ZINDEX + 7,
    closeAction: TEMP_ZINDEX + 8,
};

const mobileInteriorBackground = css<CardProps>`
    ${({ $variant }) =>
        $variant === 'fantasy'
            ? css`
                  background-color: #0b0b0c;
                  background-image: url(${bgImage});
                  background-repeat: no-repeat;
                  background-position: center center;
                  background-size: cover;
                  background-attachment: fixed;
              `
            : css`
                  background: linear-gradient(136deg, #150625 30.4%, #351b48 94.27%);
              `}
`;

export const ModalWrapper = styled(motion.div).attrs({
    variants: modalWrapperVariants,
    initial: ModalWrapperVariant.Hidden,
    animate: ModalWrapperVariant.Show,
    exit: ModalWrapperVariant.Hidden,
    transition: modalWrapperTransition,
})`
    ${styledReset.borderBox};
    ${styledLayout.positionCover({ position: LayoutPosition.Fixed })};
    z-index: ${stackingContext.backdrop};
    background-color: ${({ theme }) => theme.shades.black[55]};
`;

export const CardWrapper = styled.div`
    ${styledLayout.positionCover({ position: LayoutPosition.Fixed })};
    z-index: ${stackingContext.cardWrapper};
    display: grid;
    place-items: center;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        top: ${MODAL_MIN_GUTTER}px;
        right: ${MODAL_MIN_GUTTER}px;
        bottom: ${MODAL_MIN_GUTTER}px;
        left: ${MODAL_MIN_GUTTER}px;
    }
`;

const fantasyStyling = css<CardProps>`
    &::before,
    &::after {
        content: '';
        z-index: 1;
        position: absolute;
        top: -${MODAL_BORDER_OFFSET}px;
        right: -${MODAL_BORDER_OFFSET}px;
        bottom: -${MODAL_BORDER_OFFSET}px;
        left: -${MODAL_BORDER_OFFSET}px;
        display: block;
        border-radius: ${MODAL_BORDER_RADIUS}px;
    }

    &::before {
        z-index: ${stackingContext.cardBefore};
        background-color: #181624;
    }

    &::after {
        z-index: ${stackingContext.cardAfter};
        background: linear-gradient(
            15deg,
            rgba(82, 62, 242, 1) 8%,
            rgba(142, 114, 255, 0.1) 25%,
            rgba(220, 144, 255, 0.1) 30%,
            rgba(250, 6, 255, 1) 45%,
            rgba(220, 144, 255, 0.1) 60%,
            rgba(142, 114, 255, 0.1) 85%,
            rgba(82, 62, 242, 1) 98%
        );
    }
`;

const simpleStyling = css<CardProps>`
    border-radius: 30px;
    background: linear-gradient(136deg, #150625 30.4%, #351b48 94.27%);
`;

const desktopCardStyling = css<CardProps>`
    max-width: ${({ $maxWidth }) => ($maxWidth ? $maxWidth : `${MODAL_CARD_MAX_WIDTH}px`)};
    max-height: ${({ $maxHeight }) => $maxHeight && `${$maxHeight}px`};
    height: auto;
    z-index: ${stackingContext.card};

    ${({ $variant }) =>
        $variant === ModalVariants.Fantasy
            ? css`
                  ${fantasyStyling}
              `
            : css`
                  ${simpleStyling}
              `}
`;

export const Card = styled(motion.div).attrs({
    variants: cardVariants,
    initial: CardVariant.Hidden,
    animate: CardVariant.Show,
    exit: CardVariant.Hidden,
    transition: cardTransition,
})<CardProps>`
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: 0;
    max-height: 100%;
    height: 100%;

    ${({ $fullScreenMobile }) =>
        !$fullScreenMobile &&
        css`
            width: calc(100% - 60px);
            ${desktopCardStyling}
        `};

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        ${desktopCardStyling}
    }
`;

const desktopInteriorStyling = css`
    background: linear-gradient(134deg, #191727 30%, #351b48 104%);
    box-shadow: -16px 0 80px 16px ${({ theme }) => theme.shades.black[55]};
    border-radius: ${MODAL_BORDER_RADIUS}px;
`;

export const Interior = styled.div<FullScreenMobile>`
    ${mobileInteriorBackground};
    ${({ $mobileDarkBackground }) =>
        $mobileDarkBackground &&
        css`
            background: linear-gradient(134deg, #191727 30%, #351b48 104%);
        `};

    z-index: ${stackingContext.interior};
    position: relative;
    display: grid;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;

    ${({ $fullScreenMobile }) => !$fullScreenMobile && desktopInteriorStyling};

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        ${desktopInteriorStyling}
    }
`;

interface ContentProps {
    $padded?: boolean;
    $paddingSize: ModalPaddingSizes;
    $hasHeader?: boolean;
    $closeButtonPosition: ModalCloseButtonPositions;
}

function getGap(hasHeader, closeButtonPosition) {
    if (hasHeader && closeButtonPosition === ModalCloseButtonPositions.Header) {
        return `${MODAL_LAYOUT_SPACING}px`;
    } else if (hasHeader && closeButtonPosition === ModalCloseButtonPositions.Corner) {
        return `${MODAL_LAYOUT_SPACING}px 0`;
    }
    return 0;
}

export const Content = styled.div<ContentProps>`
    --yat_modal-padding-x: ${({ $padded, $paddingSize }) => ($padded ? `${MODAL_PADDING_SIZES.X[$paddingSize]}px` : 0)};
    --yat_modal-padding-y: ${({ $padded, $paddingSize }) => ($padded ? `${MODAL_PADDING_SIZES.Y[$paddingSize]}px` : 0)};

    position: relative;
    z-index: ${stackingContext.content};
    height: 100%;
    overflow-y: auto;
    overscroll-behavior-y: contain;

    display: grid;
    grid-template-rows: min-content min-content;
    grid-template-columns: 1fr auto;
    grid-template-areas: ${({ $closeButtonPosition }) =>
        $closeButtonPosition === ModalCloseButtonPositions.Header
            ? `'header close' 'layout layout'`
            : `'header' 'layout'`};
    gap: ${({ $hasHeader, $closeButtonPosition }) => getGap($hasHeader, $closeButtonPosition)};
    padding: calc(var(--yat_modal-padding-y) / 2) var(--yat_modal-padding-x) var(--yat_modal-padding-y)
        var(--yat_modal-padding-x);

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        ${({ $padded }) =>
            $padded
                ? css`
                      padding: ${MODAL_LAYOUT_SPACING}px;
                  `
                : ''}
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        --yat_scrollbar-thumb-color: ${({ theme }) => theme.colors.purple};

        ${styledLayout.scrollbar({
            thumbColor: 'var(--yat_scrollbar-thumb-color)',
            trackColor: 'transparent',
        })}

        ${styledLayout.featherMask({
            featherSize: MODAL_CLOSE_OFFSET * 2,
        })}
    }
`;

export const Form = styled.form`
    display: flex;
    gap: 16px;
    flex-direction: column;
`;
